import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useLiveTick } from '../hooks/useLiveTick'
import {
  hasEventEnded,
  hasEventStarted,
  isRegistrationHidden,
  isRegistrationOpen,
} from '../utils/event'
import { InvitedParticipantsTab } from '../components/InvitedParticipantsTab'
import { RegisteredParticipantsTab } from '../components/RegisteredParticipantsTab'
import { AcceptedParticipantsTab } from '../components/AcceptedParticipantsTab'
import { AttendedParticipantsTab } from '../components/AttendedParticipantsTab'
import { UnattendedParticipantsTab } from '../components/UnattendedParticipantsTab'
import { EventMediaPanel } from '../components/EventMediaPanel'
import { Button } from '../components/Button'
import { ConfirmationModal } from '../components/ConfirmationModal'
import {
  useGetEventQuery,
  useGetEventStatsQuery,
  useDeleteEventMutation,
  type GetEventStatsResponse,
} from '@proaxiv/server-api'
import { deleteEvent as deleteEventSlice } from '../store/eventsSlice'
import { useAppDispatch } from '../store/hooks'
import { fromEventSummary } from './event/eventMapping'
import type { GetEventResponse } from './event/eventMapping'
import { formatEventDateTime } from '../utils/datetime'

// ── Icons ─────────────────────────────────────────────────────────────────────

const EditIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
)

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
)

const TrashIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
)

const ExternalLinkIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
)

const CalendarIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
)

// Marks the "principal" stat/tab (Accepted Participants) so it reads as the primary
// section at a glance, not just another item in the invited/registered/attended row. See #157.
const StarIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2.5l2.9 6.4 6.9.7-5.2 4.7 1.6 6.8-6.2-3.7-6.2 3.7 1.6-6.8-5.2-4.7 6.9-.7z" />
  </svg>
)

const MapPinIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
)

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Formats an event-schedule datetime in the event's own timezone (never the viewer's),
 *  using `locale` (the app's current UI language) for month/weekday names and date order. */
function formatDateTime(iso: string, timezone: string | null | undefined, locale: string): string {
  return formatEventDateTime(iso, timezone, locale, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// ── Tab types ─────────────────────────────────────────────────────────────────

type Tab = 'invited' | 'registrations' | 'accepted' | 'attended' | 'unattended'

// ── Stat card — doubles as the tab switcher (replaces the old drawer/tab-strip) ────

function StatCard({
  label,
  value,
  active,
  primary = false,
  onClick,
}: {
  label: string
  value: string | number
  active: boolean
  /** Highlights this card as the section organizers care about most (see #157). */
  primary?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={label}
      className={[
        'focus-visible:ring-pax-blue/50 relative flex flex-col gap-1 rounded-2xl border p-4 text-left shadow-sm transition-colors focus-visible:ring-2 focus-visible:outline-none',
        active
          ? 'border-pax-blue bg-pax-light-blue cursor-pointer'
          : primary
            ? // Amber/gold rather than blue — blue is reserved for "this is the active tab", and
              // using it here too made the unselected "Accepted Participants" card easy to mistake
              // for the selected one at a glance.
              'border-pax-orange-warm/50 hover:border-pax-orange-warm ring-pax-orange-warm/15 cursor-pointer bg-white ring-1'
            : 'border-pax-purple/20 hover:border-pax-blue/40 cursor-pointer bg-white',
      ].join(' ')}
    >
      <p className="text-pax-gray flex items-center gap-1 text-xs font-medium">
        {primary && (
          <span className="text-pax-orange-warm" aria-hidden="true">
            <StarIcon />
          </span>
        )}
        {label}
      </p>
      <p className="text-pax-black text-2xl font-bold tabular-nums">{value}</p>
    </button>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

/** Per-event organizer management dashboard. Lives inside AppLayout. */
export function EventManagementPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { id } = useParams<{ id: string }>()
  // Keeps the "Registration: Open/Closed" badge (and any other deadline-derived UI below)
  // accurate for organizers who leave this page open across the registration deadline —
  // without it, the badge would freeze at whatever it computed on the last render, going
  // stale relative to the public event page which always recomputes on a fresh load.
  useLiveTick()

  // Always call the API — never trust the Redux cache alone, because the backend
  // may now return 403 if the org's eventplanner feature has been disabled since
  // the cache was populated (TC-EVT-004 fix).
  const {
    data: getEventRaw,
    isLoading: loadingEvent,
    error: getEventError,
  } = useGetEventQuery({ eventId: id! }, { skip: !id })
  const isForbidden =
    getEventError != null &&
    'status' in getEventError &&
    (getEventError.status === 403 || getEventError.status === 'PARSING_ERROR')
  const event =
    getEventRaw != null ? fromEventSummary((getEventRaw as GetEventResponse).event) : undefined

  // `refetchOnMountOrArgChange` guards against a stale RTK Query cache entry: without it, a
  // stat count fetched (and cached) before a participant's status changed elsewhere — e.g. a
  // bulk "backup register" — keeps showing its old value until *some* other mutation happens to
  // invalidate the `EventStats` tag, which reads as "the count is wrong until I force something
  // to refresh it".
  const { data: statsRaw } = useGetEventStatsQuery(
    { eventId: id! },
    { skip: !id, refetchOnMountOrArgChange: true }
  )
  const stats = (statsRaw as GetEventStatsResponse | undefined)?.stats
  const invitationCount = stats?.invitedCount ?? 0
  const registrationCount = stats?.registeredCount ?? 0
  const acceptedCount = stats?.acceptedCount ?? 0
  const attendedCount = stats?.attendedCount ?? 0

  // Attended tab is irrelevant before check-in can happen: hide it until the event has
  // started, unless someone has already been marked attended (e.g. early check-in).
  // Computed before any effects that re-measure when the tab appears/disappears.
  // hasEventStarted() (not inline Date.now()) — react-hooks/purity forbids impure calls in render.
  const eventHasStarted = event != null && hasEventStarted(event)
  const showAttendedTab = eventHasStarted || attendedCount > 0

  // Once the event has ended, "Accepted" (still-pending checked-in) participants are
  // reframed as "Unattended" — the organizer's job shifts from managing RSVPs to
  // reconciling who actually showed up. Events with no end date never gate this switch.
  const eventHasEnded = !!event && hasEventEnded(event)

  // When the organiser sets registration to "hidden" in the editor, the Invitations and
  // Registrations funnel stages no longer apply — there's nothing to invite/register into a
  // registration flow that isn't shown publicly — so those tabs are removed entirely rather
  // than shown empty.
  const regHidden = event != null && isRegistrationHidden(event)

  const VALID_TABS = new Set<Tab>([
    ...(regHidden ? [] : (['invited', 'registrations'] as Tab[])),
    ...(eventHasEnded ? (['unattended'] as Tab[]) : (['accepted'] as Tab[])),
    'attended',
  ])
  // The default/fallback tab when the URL doesn't pin a valid one — normally "Invitations",
  // but that's hidden when registration is hidden, so fall back to the next funnel stage.
  const defaultTab: Tab = regHidden ? (eventHasEnded ? 'unattended' : 'accepted') : 'invited'
  const [searchParams, setSearchParams] = useSearchParams()
  const rawTab = searchParams.get('tab') as Tab | null
  const activeTab: Tab = rawTab && VALID_TABS.has(rawTab) ? rawTab : defaultTab
  const setActiveTab = (tab: Tab) => setSearchParams({ tab }, { replace: true })
  // If ?tab=attended is in the URL but the tab is hidden, fall back to the default tab. Computed
  // here (rather than after the loading/403/404 early returns below) so every hook that depends
  // on it — see the scroll-into-view effect further down — runs on every render; a hook placed
  // after an early return fires on some renders but not others, which React flags as "Rendered
  // more hooks than during the previous render".
  const resolvedTab: Tab = activeTab === 'attended' && !showAttendedTab ? defaultTab : activeTab

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  // Event details accordion — open by default on landing; user can fold it to free space.
  const [detailsOpen, setDetailsOpen] = useState(true)
  const [deleteEvent, { isLoading: deleting }] = useDeleteEventMutation()

  // Mobile stat-tab strip scroll affordance (< / > + edge fades).
  const statScrollRef = useRef<HTMLDivElement>(null)
  const [statCanScrollLeft, setStatCanScrollLeft] = useState(false)
  const [statCanScrollRight, setStatCanScrollRight] = useState(false)

  function updateStatScrollAffordances() {
    const el = statScrollRef.current
    if (!el) return
    const { scrollLeft, scrollWidth, clientWidth } = el
    setStatCanScrollLeft(scrollLeft > 2)
    setStatCanScrollRight(scrollLeft + clientWidth < scrollWidth - 2)
  }

  function scrollStatTabs(direction: -1 | 1) {
    const el = statScrollRef.current
    if (!el) return
    el.scrollBy({ left: direction * Math.max(120, el.clientWidth * 0.6), behavior: 'smooth' })
  }

  // Redirect away from a tab that just became invalid because the event's end state
  // changed underneath the user (e.g. they left the page open across the event end time).
  useEffect(() => {
    if (rawTab === 'accepted' && eventHasEnded) {
      setSearchParams({ tab: 'unattended' }, { replace: true })
    } else if (rawTab === 'unattended' && !eventHasEnded) {
      setSearchParams({ tab: 'accepted' }, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawTab, eventHasEnded])

  useEffect(() => {
    const el = statScrollRef.current
    if (!el) return
    updateStatScrollAffordances()
    el.addEventListener('scroll', updateStatScrollAffordances, { passive: true })
    window.addEventListener('resize', updateStatScrollAffordances)
    // ResizeObserver is unavailable in some test environments — fall back to window resize.
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateStatScrollAffordances) : null
    ro?.observe(el)
    return () => {
      el.removeEventListener('scroll', updateStatScrollAffordances)
      window.removeEventListener('resize', updateStatScrollAffordances)
      ro?.disconnect()
    }
    // Re-measure when tab counts change (Attended may appear/disappear).
  }, [invitationCount, registrationCount, acceptedCount, attendedCount, showAttendedTab])

  // Keep the active pill fully in view within the mobile scroll strip. Without this, "Accepted
  // Participants" — the default/primary tab — can land partially clipped behind the right edge
  // fade on first load whenever the three-plus pills don't all fit at once, which reads as a
  // layout overflow bug rather than "just scroll right".
  useEffect(() => {
    const container = statScrollRef.current
    const activeEl = container?.querySelector<HTMLElement>(`[data-tab-id="${resolvedTab}"]`)
    if (!container || !activeEl) return
    const visibleStart = container.scrollLeft
    const visibleEnd = visibleStart + container.clientWidth
    const elStart = activeEl.offsetLeft
    const elEnd = elStart + activeEl.offsetWidth
    if (elEnd > visibleEnd) {
      container.scrollLeft += elEnd - visibleEnd
    } else if (elStart < visibleStart) {
      container.scrollLeft -= visibleStart - elStart
    }
  }, [resolvedTab])

  async function handleDeleteConfirmed() {
    if (!id) return
    try {
      await deleteEvent({ eventId: id }).unwrap()
      dispatch(deleteEventSlice(id))
      navigate('/event-planner')
    } catch (err) {
      console.error('Failed to delete event', err)
      setShowDeleteConfirm(false)
    }
  }

  // ── Loading ───────────────────────────────────────────────────────────────────
  if (loadingEvent) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-6 md:px-6 md:py-8">
        <div className="border-pax-purple/20 animate-pulse rounded-2xl border bg-white p-12 text-center">
          <div className="bg-pax-purple/10 mx-auto h-6 w-48 rounded" />
        </div>
      </div>
    )
  }

  // ── Access denied (403) ──────────────────────────────────────────────────────
  if (isForbidden) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-6 md:px-6 md:py-8">
        <Link
          to="/event-planner"
          className="text-pax-gray hover:text-pax-blue mb-8 flex items-center gap-1.5 text-sm transition-colors focus-visible:outline-none"
        >
          ← {t('ep_title')}
        </Link>
        <div className="border-pax-purple/20 flex flex-col items-center gap-4 rounded-2xl border bg-white p-12 text-center">
          <p className="text-pax-gray text-5xl font-black opacity-20">403</p>
          <p className="text-pax-black text-lg font-semibold">Access denied</p>
          <p className="text-pax-gray text-sm">
            The Event Planner feature is not enabled for this organisation.
          </p>
        </div>
      </div>
    )
  }

  // ── Not found ────────────────────────────────────────────────────────────────
  if (!event) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-6 md:px-6 md:py-8">
        <Link
          to="/event-planner"
          className="text-pax-gray hover:text-pax-blue mb-8 flex items-center gap-1.5 text-sm transition-colors focus-visible:outline-none"
        >
          ← {t('ep_title')}
        </Link>
        <div className="border-pax-purple/20 flex flex-col items-center gap-4 rounded-2xl border bg-white p-12 text-center">
          <p className="text-pax-gray text-5xl font-black opacity-20">404</p>
          <p className="text-pax-black text-lg font-semibold">Event not found</p>
          <p className="text-pax-gray text-sm">This event does not exist or has been deleted.</p>
        </div>
      </div>
    )
  }

  const regOpen = isRegistrationOpen(event)
  const eventEnded = hasEventEnded(event)

  // ── Stat boxes — these double as the only tab switcher on both mobile and desktop ──

  // "Accepted Participants" is the principal section before the event ends — the other funnel
  // stages (invited/registrations/attended) are secondary. Flagging it as `primary` lets both
  // the mobile pill and desktop stat card highlight it so it doesn't read as just another item
  // in the row. See #157. Once the event has ended, the organizer's focus shifts from managing
  // RSVPs to reconciling who actually showed up, so the star moves onto "Attended" instead —
  // "Unattended" (leftover accepted-but-not-checked-in participants) is no longer the section to
  // spotlight at that point.
  const statTabs: { id: Tab; label: string; value: number; primary?: boolean }[] = [
    ...(regHidden
      ? []
      : [
          { id: 'invited' as const, label: t('mgmt_tab_invited'), value: invitationCount },
          {
            id: 'registrations' as const,
            label: t('mgmt_tab_registrations'),
            value: registrationCount,
          },
        ]),
    eventHasEnded
      ? { id: 'unattended' as const, label: t('mgmt_tab_unattended'), value: acceptedCount }
      : {
          id: 'accepted' as const,
          label: t('mgmt_tab_accepted'),
          value: acceptedCount,
          primary: true,
        },
    ...(showAttendedTab
      ? [
          {
            id: 'attended' as const,
            label: t('mgmt_tab_attended'),
            value: attendedCount,
            primary: eventHasEnded,
          },
        ]
      : []),
  ]

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-6 md:px-6 md:py-8">
      {/* Breadcrumb */}
      <Link
        to="/event-planner"
        className="text-pax-gray hover:text-pax-blue flex items-center gap-1.5 text-sm font-medium transition-colors focus-visible:outline-none"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M19 12H5M12 5l-7 7 7 7" />
        </svg>
        {t('ep_title')}
      </Link>

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-pax-black text-2xl font-bold">{event.title}</h1>
        </div>

        {/* Mobile: 2×2 grid so the four actions stay fully visible; desktop: single row */}
        <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center [&>*]:w-full sm:[&>*]:w-auto">
          <Link
            to={`/e/${event.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-pax-blue border-pax-blue/30 hover:bg-pax-light-blue flex items-center justify-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none sm:justify-start"
          >
            <ExternalLinkIcon />
            {t('ep_view_public')}
          </Link>
          <Link
            to={`/event-planner/${event.id}/verify`}
            className="text-pax-green border-pax-green/30 hover:bg-pax-green/8 flex items-center justify-center gap-1.5 rounded-lg border bg-white px-2.5 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none sm:justify-start"
            aria-label={t('mgmt_tab_verification')}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="9 11 12 14 22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
            {t('mgmt_tab_verification')}
          </Link>
          <Link
            to={`/event-planner/${event.id}/presentations`}
            className="text-pax-purple border-pax-purple/30 hover:bg-pax-purple/8 flex items-center justify-center gap-1.5 rounded-lg border bg-white px-2.5 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none sm:justify-start"
            aria-label={t('mgmt_tab_presentations_archive')}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            {t('mgmt_tab_presentations_archive')}
          </Link>
          <Link
            to={`/event-planner/${event.id}/edit`}
            className="text-pax-gray hover:text-pax-blue border-pax-purple/30 flex items-center justify-center gap-1.5 rounded-lg border bg-white px-2.5 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none sm:justify-start"
          >
            <EditIcon />
            {t('ep_edit')}
          </Link>
          <Button
            variant="danger"
            size="sm"
            leftIcon={<TrashIcon />}
            onClick={() => setShowDeleteConfirm(true)}
            disabled={deleting}
            aria-label={t('mgmt_delete_event')}
          >
            {t('ep_delete')}
          </Button>
        </div>
      </div>

      {/* Event details — foldable accordion; open by default when landing on the page */}
      <div className="border-pax-purple/20 rounded-2xl border bg-white shadow-sm">
        <button
          type="button"
          onClick={() => setDetailsOpen((o) => !o)}
          aria-expanded={detailsOpen}
          className={[
            'flex w-full cursor-pointer items-center justify-between gap-3 px-6 py-4 text-left transition-colors focus-visible:outline-none',
            detailsOpen ? 'border-pax-purple/20 border-b' : '',
          ].join(' ')}
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <p className="text-pax-gray text-xs font-medium tracking-wider uppercase">
              {t('ep_section_details')}
            </p>
            {/* Draft/Published lives here, right beside the registration badge, rather than up
                next to the title — the two badges are both "event state at a glance" signals and
                read better grouped together than split across the page. */}
            <span
              className={[
                'w-fit shrink-0 rounded-full px-3 py-1 text-xs font-semibold',
                event.status === 'published'
                  ? 'bg-pax-green/15 text-pax-green'
                  : 'bg-pax-orange-warm/15 text-pax-orange-warm',
              ].join(' ')}
            >
              {event.status === 'published' ? t('ep_status_published') : t('ep_status_draft')}
            </span>
            {/* Once the event's end time has passed, "Registration: Open/Closed" is no longer
                meaningful — show a neutral "Event finished" badge instead regardless of the
                underlying registration-open computation. */}
            {eventEnded ? (
              <span className="bg-pax-gray/10 text-pax-gray w-fit rounded-full px-3 py-1 text-xs font-semibold">
                {t('mgmt_event_finished')}
              </span>
            ) : (
              <span
                className={[
                  'w-fit rounded-full px-3 py-1 text-xs font-semibold',
                  regOpen ? 'bg-pax-green/15 text-pax-green' : 'bg-pax-red/10 text-pax-red',
                ].join(' ')}
              >
                {t('mgmt_stat_registration')}:{' '}
                <span>{regOpen ? t('mgmt_reg_open') : t('mgmt_reg_closed')}</span>
              </span>
            )}
          </div>
          <span className="text-pax-gray shrink-0">
            <ChevronIcon open={detailsOpen} />
          </span>
        </button>
        {detailsOpen && (
          <div className="flex flex-col gap-3 px-6 py-4">
            {event.startDateTime && (
              <div className="flex items-start gap-3">
                <span className="text-pax-blue mt-0.5 shrink-0">
                  <CalendarIcon />
                </span>
                <div>
                  <p className="text-pax-black text-sm font-medium">
                    {formatDateTime(event.startDateTime, event.timezone, i18n.language)}
                  </p>
                  {event.endDateTime && (
                    <p className="text-pax-gray text-xs">
                      → {formatDateTime(event.endDateTime, event.timezone, i18n.language)}
                    </p>
                  )}
                </div>
              </div>
            )}
            {event.address && (
              <div className="flex items-start gap-3">
                <span className="text-pax-blue mt-0.5 shrink-0">
                  <MapPinIcon />
                </span>
                <p className="text-pax-black text-sm">{event.address}</p>
              </div>
            )}
            {event.text && (
              <div className="border-pax-purple/20 mt-2 rounded-xl border bg-slate-50 p-3 text-sm leading-relaxed whitespace-pre-wrap">
                {event.text}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Post-event media upload (EP-14) — only relevant once the event has actually happened,
          matching the BPMN post-event process. Lives under Event Details (always visible), not
          inside the mobile-only stat strip, so it's reachable on desktop too. */}
      {eventHasEnded && <EventMediaPanel event={event} />}

      {/* Stat boxes — the only tab switcher (replaces the old drawer/tab-strip). Persistent:
           they stay visible while the content below them switches to match the active tab.
           Mobile: horizontal slider with < / > and edge fades so scrollability is obvious;
           desktop keeps the larger grid, with tabs removed entirely to match. */}
      <div data-testid="stat-nav-mobile" className="relative flex items-center gap-0.5 sm:hidden">
        <button
          type="button"
          onClick={() => scrollStatTabs(-1)}
          disabled={!statCanScrollLeft}
          aria-label="Scroll tabs left"
          className={[
            'flex h-9 w-7 shrink-0 items-center justify-center rounded-lg text-base font-semibold transition-colors focus-visible:outline-none',
            statCanScrollLeft
              ? 'text-pax-blue hover:bg-pax-light-blue cursor-pointer'
              : 'text-pax-gray/30 cursor-default',
          ].join(' ')}
        >
          &lt;
        </button>

        <div className="relative min-w-0 flex-1">
          {/* Edge fades — signal that more tabs sit beyond the visible strip */}
          <div
            aria-hidden="true"
            className={[
              'from-pax-light-purple pointer-events-none absolute inset-y-0 left-0 z-10 w-6 bg-gradient-to-r to-transparent transition-opacity',
              statCanScrollLeft ? 'opacity-100' : 'opacity-0',
            ].join(' ')}
          />
          <div
            aria-hidden="true"
            className={[
              'from-pax-light-purple pointer-events-none absolute inset-y-0 right-0 z-10 w-6 bg-gradient-to-l to-transparent transition-opacity',
              statCanScrollRight ? 'opacity-100' : 'opacity-0',
            ].join(' ')}
          />

          <div
            ref={statScrollRef}
            data-testid="stat-nav-scroll"
            className="flex snap-x snap-mandatory [scrollbar-width:none] gap-2 overflow-x-auto px-1 py-0.5 [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            {statTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                data-tab-id={tab.id}
                onClick={() => setActiveTab(tab.id)}
                aria-pressed={resolvedTab === tab.id}
                aria-label={tab.label}
                className={[
                  // `snap-start` pins each tile to the strip's scroll-snap points (see #228):
                  // without it, the strip could come to rest mid-tile, clipping its label
                  // (e.g. "Accepted Participants" cut down to "Acc") instead of showing either
                  // the full tile or none of it.
                  'focus-visible:ring-pax-blue/50 flex shrink-0 cursor-pointer snap-start items-center rounded-xl border px-3 py-2 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none',
                  resolvedTab === tab.id
                    ? 'border-pax-blue bg-pax-light-blue'
                    : tab.primary
                      ? // Amber/gold rather than blue — see the desktop StatCard above for why.
                        'border-pax-orange-warm/50 ring-pax-orange-warm/15 bg-white ring-1'
                      : 'border-pax-purple/20 hover:border-pax-blue/40 bg-white',
                ].join(' ')}
              >
                <span className="flex flex-col leading-tight whitespace-nowrap">
                  <span className="text-pax-black text-sm font-bold tabular-nums">{tab.value}</span>
                  <span className="text-pax-gray flex items-center gap-0.5 text-[10px] font-medium">
                    {tab.primary && (
                      <span className="text-pax-orange-warm" aria-hidden="true">
                        <StarIcon />
                      </span>
                    )}
                    {tab.label}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={() => scrollStatTabs(1)}
          disabled={!statCanScrollRight}
          aria-label="Scroll tabs right"
          className={[
            'flex h-9 w-7 shrink-0 items-center justify-center rounded-lg text-base font-semibold transition-colors focus-visible:outline-none',
            statCanScrollRight
              ? 'text-pax-blue hover:bg-pax-light-blue cursor-pointer'
              : 'text-pax-gray/30 cursor-default',
          ].join(' ')}
        >
          &gt;
        </button>
      </div>

      <div
        data-testid="stat-nav-desktop"
        className={[
          'hidden gap-4 sm:grid',
          statTabs.length === 4
            ? 'grid-cols-4'
            : statTabs.length === 3
              ? 'grid-cols-3'
              : statTabs.length === 2
                ? 'grid-cols-2'
                : 'grid-cols-1',
        ].join(' ')}
      >
        {statTabs.map((tab) => (
          <StatCard
            key={tab.id}
            label={tab.label}
            value={tab.value}
            active={resolvedTab === tab.id}
            primary={tab.primary}
            onClick={() => setActiveTab(tab.id)}
          />
        ))}
      </div>

      {/* Tab content */}
      {resolvedTab === 'invited' && <InvitedParticipantsTab event={event} />}
      {resolvedTab === 'registrations' && <RegisteredParticipantsTab event={event} />}
      {resolvedTab === 'accepted' && !eventHasEnded && <AcceptedParticipantsTab event={event} />}
      {resolvedTab === 'unattended' && eventHasEnded && <UnattendedParticipantsTab event={event} />}
      {resolvedTab === 'attended' && showAttendedTab && <AttendedParticipantsTab event={event} />}

      {showDeleteConfirm && (
        <ConfirmationModal
          title={t('ep_delete_confirm_title')}
          notify={false}
          onConfirm={handleDeleteConfirmed}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  )
}
